.notification {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-12);
  /* Figma spec: padding 16 / 16, --bg-secondary background,
   * --radius-8, fluid width (Figma uses 360px fixed but consumers
   * size via the parent layout). */
  padding: var(--spacing-16);
  border-radius: var(--radius-8);
  background: var(--bg-secondary);
  width: 100%;
  box-sizing: border-box;
}

/* Variant slots — Default + Phone call + Missed call + Conversation.
 * Small media and Rich media are NOT variants — they're slot compositions
 * on the Default variant via the `media` and `richMedia` props (see
 * the TSX JSDoc for the architecture rationale). */
.variant-default {
  /* No overrides; the base .notification block IS the default
   * variant. Class exists so `styles[\`variant-${variant}\`]` looks
   * up to a real selector instead of `undefined`. Same trick the
   * IconButton primitive uses for its variant-default empty class. */
}

/* Conversation variant: same column flex as default, with the message
 * list slotted between the body and the reply composer. No layout
 * overrides needed — the base column flex handles it. The class exists
 * so the variant lookup resolves to a real selector. */
.variant-conversation {
  /* No structural overrides — base .notification column flex is correct.
   * Message-specific rules live on .messageList / .messageRow below. */
}

/* Phone call layout: horizontal row with caller name + status on
 * the left, answer + decline IconButtons on the right. Override
 * the base column flex to lay the children out in a row. */
.variant-phoneCall {
  flex-direction: row;
  align-items: center;
  /* gap between the call body block and the action buttons cluster */
  gap: var(--spacing-12);
}

/* Missed call body text uses --text-negative instead of the default
 * --text-tertiary. Targets the bodyText class inside the missed-call
 * variant only — Default's body styling stays intact.
 *
 * The two-line clamp inherited from the base `.bodyText` rule
 * (display: -webkit-box; -webkit-line-clamp: 2; etc.) applies here
 * too — intentional, matches the default variant's two-line cap.
 * Consumers passing a long status string (e.g. "Missed call · rang
 * for 18 seconds, voicemail available · 555-1234") will see
 * truncation at the same threshold as the default variant. */
.variant-missedCall .bodyText {
  color: var(--text-negative);
}

/* ─── Header row (app icon + meta + close) ──────────────────────── */

.headerRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: var(--spacing-8);
}

.headerMeta {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  /* Allow the meta block to shrink if the close button needs space —
   * min-width:0 unlocks shrinking for the truncating children. */
  min-width: 0;
  flex: 1 1 auto;
}

.appIcon {
  width: 18px;
  height: 18px;
  border-radius: var(--radius-circle);
  flex-shrink: 0;
  object-fit: cover;
}

/* App name + separator + timestamp share the same Medium/Micro type:
 * 12px / 20px line-height / 500 weight / --text-xs-tracking — same as
 * Button xs and Link primitive (Figma "tracking: 1" at 12px = 0.12px). */
.appName,
.separator,
.timestamp {
  font: var(--text-micro-medium);
  letter-spacing: var(--text-xs-tracking);
  flex-shrink: 0;
}

/* App name + separator are slightly more prominent than the
 * timestamp in the Figma spec — light mode uses --text-secondary
 * (#404040) for the name and --text-tertiary (#525252) for the
 * timestamp. Dark mode collapses both to --text-tertiary (#a3a3a3)
 * in Figma; we use --text-secondary (#d4d4d4) and --text-tertiary
 * (#a3a3a3) consistently across modes — close enough to the spec
 * without hardcoding per-mode literals. */
.appName,
.separator {
  color: var(--text-secondary);
}

.timestamp {
  color: var(--text-tertiary);
}

/* ─── Body row (body + optional inline media slot) ─────────────── */

/* Body row is a horizontal flex container holding the body block
 * (title + description) plus an optional inline media thumbnail
 * (the Figma "Small media" composition). When `media` is undefined
 * the row degenerates to just the body — visually identical to the
 * pre-PR layout. */
.bodyRow {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-8);
  width: 100%;
}

/* ─── Body (title + description) ───────────────────────────────── */

.body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  /* Grow to fill the body row when there's no media slot, shrink
   * to make room when there is. min-width:0 unlocks shrinking for
   * the truncating description text. */
  flex: 1 1 auto;
  min-width: 0;
}

.title {
  font: var(--text-body-semibold);
  color: var(--text-primary);
  /* <h3> default browser margin; reset to keep the gap controlled
   * by the parent `.body` flex gap, not heading defaults. */
  margin: 0;
}

.bodyText {
  font: var(--text-micro-regular);
  letter-spacing: var(--text-xs-tracking);
  color: var(--text-tertiary);
  margin: 0;
  /* Two-line clamp matching the Figma "Researchers are discovering
   * how music affects the brain, helping us…" truncation. WebKit
   * line-clamp is supported in all modern Chromium (the only
   * browser this extension targets). */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Bodies with explicit \n (InboxStyle): honour line breaks and relax
 * the clamp to 5. Compound selector locks specificity above
 * `.bodyText` so the override survives stylesheet reordering, and
 * pre-line stays scoped to multiline cards so single-paragraph bodies
 * keep `white-space: normal`. */
.bodyText.bodyTextMultiline {
  white-space: pre-line;
  -webkit-line-clamp: 5;
}

/* ─── Media slots ──────────────────────────────────────────────── */

/* Inline 48×48 thumbnail (Figma "Small media" composition). Sits
 * to the right of the body block in the bodyRow. radius-12 to match
 * the Figma spec. The slot accepts arbitrary ReactNode — overflow
 * is clipped so an oversized child doesn't break the card layout. */
.mediaSlot {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-12);
  flex-shrink: 0;
  overflow: hidden;
}

/* Full-width 164px-tall block (Figma "Rich media" composition).
 * Sits below the bodyRow, above the actions row. Lives in the
 * outer card flex column (NOT the bodyRow), so it spans the full
 * card width. radius-8 to match the Figma spec. Overflow clipped
 * for the same reason as .mediaSlot. */
.richMediaSlot {
  width: 100%;
  height: 164px;
  border-radius: var(--radius-8);
  flex-shrink: 0;
  overflow: hidden;
}

/* ─── Action buttons row ───────────────────────────────────────── */

.actions {
  display: flex;
  gap: var(--spacing-8);
  align-items: flex-start;
  flex-wrap: wrap;
}

/* ─── Reply slot ───────────────────────────────────────────────── */

/* Layout hole for consumer-supplied reply UI (controlled textarea +
 * send button is the canonical case, but the slot accepts anything).
 * The container provides ONLY width and the same flex-flow context
 * the rest of the article uses — no padding, no border, no background.
 * Visual chrome is the consumer's call so they can match their own
 * design language (borderless inline reply, bordered card, inset
 * tray, etc.).
 *
 * The reply slot lives BELOW the actions row, in the outer card
 * flex column (NOT inside the bodyRow). It's the last child of the
 * article so a long reply textarea pushes everything above it
 * upward rather than wrapping inline. */
.replySlot {
  width: 100%;
}

/* ─── Phone call variant ───────────────────────────────────────── */

/* Caller name + status subtitle column, sits to the left of the
 * answer/decline buttons. Grows to fill the available width so the
 * action buttons hug the right edge. min-width:0 unlocks shrinking
 * if the caller name is long. */
.callBody {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  flex: 1 1 auto;
  min-width: 0;
}

/* Subtitle ("Incoming call…") rendered in --text-success so the
 * live state reads as positive/active. Same Regular/Micro type as
 * the missed-call body. */
.callSubtitle {
  font: var(--text-micro-regular);
  letter-spacing: var(--text-xs-tracking);
  color: var(--text-success);
  margin: 0;
}

/* Answer + decline button cluster on the right. Fixed width — the
 * two round IconButtons + their inter-button gap. */
.callActions {
  display: flex;
  gap: var(--spacing-8);
  align-items: center;
  flex-shrink: 0;
}

/* Answer (green) and decline (red) buttons override IconButton's
 * default flat style with a fully rounded, colored background. The
 * IconButton primitive doesn't expose a "round colored" variant, so
 * we override via cascade — the `:not(:disabled)` selector keeps the
 * rules below the IconButton's own disabled treatment.
 *
 * Cascade dependency: the `:hover` rules below override IconButton's
 * own `:hover` background because they target the same element with
 * a more specific selector. If a future refactor changes IconButton's
 * `:hover` rule to use `!important` or moves it into a higher-priority
 * cascade layer, these overrides will silently stop working — keep
 * the cascade contract in mind when touching either file. */
.answerButton:not(:disabled) {
  /* --semantic-success is #10b981 in BOTH light and dark modes (no
   * dark-mode shift in tokens-figma.css), matching the Figma spec
   * which uses the same green for both themes. The icon glyph
   * color is --icon-on-dark (white) per the Figma file.
   *
   * Known a11y concern: white on #10b981 measures roughly 2.5:1
   * which is below WCAG 1.4.11 non-text contrast (3:1). The Figma
   * spec uses this exact pairing, so we ship-as-designed and flag
   * it for the design team to revisit alongside other contrast
   * passes. TODO: when design exposes a darker --button-success
   * token (or accepts a darker glyph), swap and remove this note. */
  background: var(--semantic-success);
  border-radius: var(--radius-round);
  color: var(--icon-on-dark);
}
/* Override IconButton's default hover background with a brightness
 * filter so the green stays green (not the icon-bg-hover gray that
 * the default variant would otherwise apply). */
.answerButton:not(:disabled):hover {
  background: var(--semantic-success);
  filter: brightness(0.95);
}

.declineButton:not(:disabled) {
  /* --button-negative is #f55951 in both modes (no dark shift),
   * matching the Figma spec. Same contrast caveat as the answer
   * button — white on #f55951 is roughly 3.0:1, marginal at the
   * WCAG 1.4.11 threshold. Same TODO. */
  background: var(--button-negative);
  border-radius: var(--radius-round);
  color: var(--icon-on-dark);
}
/* Same cascade override pattern as .answerButton:hover — keep the
 * red red, don't fall back to icon-bg-hover gray. */
.declineButton:not(:disabled):hover {
  background: var(--button-negative);
  filter: brightness(0.95);
}

/* ─── Conversation variant — message list ──────────────────────── */

/* Message list: an unstyled <ul> reset (browser default adds bullet
 * points + padding that we don't want) laid out as a flex column.
 * Row-to-row gap matches the outer card gap (12px / --spacing-12). */
.messageList {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-12);
  /* Reset browser <ul> defaults */
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
}

/* Each message row: Avatar (28px) + text block side by side.
 * gap: 12px between avatar and text block per Figma spec. */
.messageRow {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: var(--spacing-12);
}

/* Text block: sender name + message text stacked vertically.
 * flex: 1 1 auto lets it fill remaining width so long messages wrap
 * instead of overflowing the row. min-width: 0 unlocks shrinking for
 * the wrapping text. */
.messageContent {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  flex: 1 1 auto;
  min-width: 0;
}

/* Sender name: semibold, 14px/20px — same weight token as .title but
 * at a smaller base size matching the Figma spec. */
.senderName {
  font: var(--text-body-semibold);
  font-size: 14px;
  line-height: 20px;
  color: var(--text-primary);
}

/* Message text: regular, 14px/20px — same size as senderName but
 * regular weight, tertiary tone matching the Figma spec. */
.messageText {
  font: var(--text-body-regular);
  font-size: 14px;
  line-height: 20px;
  color: var(--text-tertiary);
  /* Allow long messages to wrap naturally — no line clamp so the full
   * message is always readable in the thread. */
  word-break: break-word;
}


